import { DiscountUpdateEvent } from '@theme/events';

const STORAGE_KEY = 'co_promotion_discount_codes';

function normalizeDiscountCode(code) {
  return String(code || '').trim().toUpperCase();
}

function getCartUpdateUrl() {
  if (typeof Theme !== 'undefined' && Theme.routes?.cart_update_url) {
    return Theme.routes.cart_update_url;
  }

  return '/cart/update.js';
}

function getStoredDiscountCodes() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(stored) ? stored.map(normalizeDiscountCode).filter(Boolean) : [];
  } catch {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage can be unavailable in private browsing; the in-memory state still works.
    }
    return [];
  }
}

function replaceStoredDiscountCodes(codes) {
  const normalizedCodes = codes.map(normalizeDiscountCode).filter(Boolean);

  if (normalizedCodes.length === 0) {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage can be unavailable in private browsing; clearing is best-effort.
    }
    return;
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(new Set(normalizedCodes))));
  } catch {
    // Storage can be unavailable in private browsing; persistence is best-effort.
  }
}

function getDiscountCodes(cart) {
  const codes = [];

  if (Array.isArray(cart?.discount_codes)) {
    cart.discount_codes.forEach((discount) => {
      if (discount?.code && discount?.applicable !== false) codes.push(discount.code);
    });
  }

  if (Array.isArray(cart?.cart_level_discount_applications)) {
    cart.cart_level_discount_applications.forEach((discount) => {
      if (discount?.title) codes.push(discount.title);
    });
  }

  return Array.from(new Set(codes.map(normalizeDiscountCode).filter(Boolean)));
}

async function getCart() {
  const response = await fetch('/cart.js', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  if (!response.ok) {
    throw new Error(`Cart fetch failed: ${response.status}`);
  }

  return response.json();
}

function setPromotionApplied(container, isApplied) {
  const button = container.querySelector('[data-promotion-badge]');
  const applied = container.querySelector('[data-promotion-applied]');
  const isProductCard = container.classList.contains('custom-promotion--product-card');
  const code = normalizeDiscountCode(container.dataset.discountCode);

  if (button instanceof HTMLElement) {
    button.hidden = isProductCard ? false : isApplied;
    button.dataset.promotionAppliedState = isApplied ? 'true' : 'false';
    button.removeAttribute('aria-busy');
    button.removeAttribute('data-promotion-error');

    if (isProductCard && isApplied) {
      button.setAttribute('aria-label', `Code ${code} applied`);
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-label');
      button.removeAttribute('aria-disabled');
    }
  }

  if (applied instanceof HTMLElement) {
    applied.hidden = !isApplied;
  }
}

function updatePromotionDisplays(code, isApplied) {
  const normalizedCode = normalizeDiscountCode(code);
  if (!normalizedCode) return;

  document.querySelectorAll('[data-promotion-container]').forEach((container) => {
    if (!(container instanceof HTMLElement)) return;
    if (normalizeDiscountCode(container.dataset.discountCode) !== normalizedCode) return;

    setPromotionApplied(container, isApplied);
  });

  document.querySelectorAll('[data-cart-discount-suggestion]').forEach((notice) => {
    if (!(notice instanceof HTMLElement)) return;
    if (normalizeDiscountCode(notice.dataset.discountCode) !== normalizedCode) return;

    const action = notice.querySelector('[data-promotion-apply]');
    const applied = notice.querySelector('[data-cart-discount-applied]');

    if (action instanceof HTMLElement) action.hidden = isApplied;
    if (applied instanceof HTMLElement) applied.hidden = !isApplied;
  });
}

function focusCartPromotionStatus(notice, code) {
  const normalizedCode = normalizeDiscountCode(code);
  if (!(notice instanceof HTMLElement) || normalizeDiscountCode(notice.dataset.discountCode) !== normalizedCode) return;
  const applied = notice?.querySelector('[data-cart-discount-applied]');

  if (applied instanceof HTMLElement) applied.focus();
}

function setButtonLoading(button, isLoading) {
  button.disabled = isLoading;

  if (isLoading) {
    button.setAttribute('aria-busy', 'true');
    button.removeAttribute('data-promotion-error');
  } else {
    button.removeAttribute('aria-busy');
  }
}

function markButtonError(button) {
  button.setAttribute('data-promotion-error', 'true');
}

async function applyPromotionDiscount(button, code) {
  const normalizedCode = normalizeDiscountCode(code);
  if (!normalizedCode || button.disabled || button.dataset.promotionAppliedState === 'true') return;

  setButtonLoading(button, true);
  const restoreFocus = document.activeElement === button;
  const originatingNotice = button.closest('[data-cart-discount-suggestion]');

  try {
    let existingCodes = [];

    try {
      existingCodes = getDiscountCodes(await getCart());
    } catch {
      existingCodes = getStoredDiscountCodes();
    }

    if (!existingCodes.includes(normalizedCode)) {
      existingCodes.push(normalizedCode);
    }

    const response = await fetch(getCartUpdateUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        discount: existingCodes.join(','),
      }),
    });

    await response.json();

    if (!response.ok) {
      throw new Error(`Discount update failed: ${response.status}`);
    }

    const verifiedCart = await getCart();
    const verifiedCodes = getDiscountCodes(verifiedCart);

    if (!verifiedCodes.includes(normalizedCode)) {
      throw new Error('Discount update was not confirmed by Shopify');
    }

    replaceStoredDiscountCodes(verifiedCodes);
    updatePromotionDisplays(normalizedCode, true);
    document.dispatchEvent(new DiscountUpdateEvent(verifiedCart, 'custom-promotion'));

    if (window.CO?.events) {
      window.CO.events.emit('co:promotion-applied', { code: normalizedCode, cart: verifiedCart });
    }

    if (restoreFocus && originatingNotice instanceof HTMLElement) {
      requestAnimationFrame(() => focusCartPromotionStatus(originatingNotice, normalizedCode));
    }
  } catch (error) {
    markButtonError(button);

    if (window.theme?.designMode || window.isDevelopmentMode) {
      console.warn('[CO] Failed to apply promotion discount:', error);
    }
  } finally {
    setButtonLoading(button, false);
  }
}

function syncFromCartPayload(cart) {
  if (!cart || (!Array.isArray(cart.discount_codes) && !Array.isArray(cart.cart_level_discount_applications))) {
    return;
  }

  const codes = getDiscountCodes(cart);
  replaceStoredDiscountCodes(codes);

  document.querySelectorAll('[data-promotion-container]').forEach((container) => {
    if (!(container instanceof HTMLElement)) return;

    const code = normalizeDiscountCode(container.dataset.discountCode);
    setPromotionApplied(container, codes.includes(code));
  });
}

async function initStoredPromotionState() {
  const observer = new MutationObserver(syncStoredPromotionDisplays);
  observer.observe(document.body, { childList: true, subtree: true });

  try {
    syncFromCartPayload(await getCart());
    syncStoredPromotionDisplays();
  } catch {
    syncStoredPromotionDisplays();
  }
}

function syncStoredPromotionDisplays() {
  getStoredDiscountCodes().forEach((code) => {
    updatePromotionDisplays(code, true);
  });
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('[data-promotion-apply]');
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  event.stopPropagation();

  applyPromotionDiscount(button, button.dataset.discountCode);
});

document.addEventListener('discount:update', (event) => {
  syncFromCartPayload(event.detail?.resource);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStoredPromotionState, { once: true });
} else {
  initStoredPromotionState();
}
